import {RefObject, useLayoutEffect, useState} from "react";

function getScrollParent(el: HTMLElement | null): HTMLElement | null {
  let node = el?.parentElement ?? null;
  while (node && node !== document.documentElement) {
    const {overflow, overflowY} = window.getComputedStyle(node);
    if (
      /(auto|scroll|overlay)/.test(overflowY) ||
      /(auto|scroll|overlay)/.test(overflow)
    ) {
      return node;
    }
    node = node.parentElement;
  }
  return null;
}

/**
 * True when the cards region has scrolled up so content sits beneath the sticky column header
 * (same moment a divider under the header is useful).
 */
export function useStickyColumnHeaderDivider(
  headerRef: RefObject<HTMLElement>,
  cardsPaddingRef: RefObject<HTMLElement>,
): boolean {
  const [scrolledUnder, setScrolledUnder] = useState(false);

  useLayoutEffect(() => {
    const header = headerRef.current;
    const cards = cardsPaddingRef.current;
    if (!header || !cards) {
      return undefined;
    }

    const scrollRoot = getScrollParent(header);
    if (!scrollRoot) {
      return undefined;
    }

    let frame = 0;
    const tick = () => {
      const headerRect = header.getBoundingClientRect();
      const cardsRect = cards.getBoundingClientRect();
      setScrolledUnder(cardsRect.top < headerRect.bottom - 0.5);
    };

    const onScrollOrResize = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(tick);
    };

    tick();
    scrollRoot.addEventListener("scroll", onScrollOrResize, {passive: true});
    window.addEventListener("resize", onScrollOrResize);

    const resizeObserver =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(onScrollOrResize)
        : null;
    resizeObserver?.observe(header);
    resizeObserver?.observe(cards);

    return () => {
      cancelAnimationFrame(frame);
      scrollRoot.removeEventListener("scroll", onScrollOrResize);
      window.removeEventListener("resize", onScrollOrResize);
      resizeObserver?.disconnect();
    };
  }, [headerRef, cardsPaddingRef]);

  return scrolledUnder;
}
